import { useState, useRef, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  MessageCircle,
  Send,
  Loader2,
  FileText,
  Sparkles,
  ExternalLink,
  X,
  ChevronRight,
  File,
  Copy,
  Check,
  Download,
  FileDown,
  FileSpreadsheet,
  FileType,
  History,
  Plus,
  Trash2,
  BarChart3,
  Link2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { clientsService } from '@/services/api/clients';
import { exportToPdf, exportToWord, exportToCsv, exportToExcel } from '@/utils/chat-export';
import type { ChatMessage, DocumentCitation } from '@/types/client';

interface ClientFileChatSidebarProps {
  clientId: string;
  clientName: string;
  /** Which accounting system this client is connected to (drives the toggle label). */
  accountingProvider?: 'xero' | 'qbo' | null;
}

// Citation detail dialog component
function CitationDialog({
  citation,
  index,
  open,
  onOpenChange,
}: {
  citation: DocumentCitation;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [downloading, setDownloading] = useState(false);

  const isBenchmark = citation.source === 'benchmark';

  const handleViewDocument = async () => {
    if (isBenchmark && citation.sourceUrl) {
      window.open(citation.sourceUrl, '_blank');
      return;
    }
    try {
      setDownloading(true);
      const data = await clientsService.getFileDownloadUrl(citation.documentId);
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.fileName || citation.documentName;
      link.click();
    } catch (err) {
      toast.error('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (isBenchmark && citation.sourceUrl) {
      window.open(citation.sourceUrl, '_blank');
      return;
    }
    try {
      setDownloading(true);
      const data = await clientsService.getFileDownloadUrl(citation.documentId);
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      toast.error('Failed to open file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-primary" />
            Source [{index}]
          </DialogTitle>
          <DialogDescription>Document citation details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Document name */}
          <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
            <File className="h-8 w-8 text-red-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm">{citation.documentName}</p>
              {citation.pageNumber && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Page {citation.pageNumber}
                </p>
              )}
              <Badge variant="secondary" className="mt-2 text-xs">
                {Math.round(citation.relevanceScore * 100)}% match
              </Badge>
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              RELEVANT EXCERPT
            </p>
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg overflow-x-auto max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full" style={{ scrollbarWidth: 'thin' }}>
              <p className="text-sm leading-relaxed italic whitespace-pre-wrap">"{citation.excerpt}"</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={handleViewDocument} disabled={downloading}>
              {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
              Download
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={handleOpenInNewTab} disabled={downloading}>
              {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
              Open in New Tab
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline citation badge component
function InlineCitation({
  index,
  citation,
  onClick,
}: {
  index: number;
  citation: DocumentCitation;
  onClick: () => void;
}) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={200} cursor={false}>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className="inline-flex items-center justify-center h-4 min-w-4 px-1 ml-0.5 text-[9px] font-bold bg-primary/20 text-primary hover:bg-primary/30 rounded-sm cursor-pointer transition-colors align-super"
          >
            {index}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-semibold text-xs">{citation.documentName}</p>
          {citation.pageNumber && (
            <p className="text-xs text-muted-foreground">Page {citation.pageNumber}</p>
          )}
          <p className="text-xs mt-1 line-clamp-2 italic">"{citation.excerpt}"</p>
          <p className="text-[10px] text-muted-foreground mt-1">Click for details</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Chart colors
const CHART_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];

// Format chart values - detect if values are currency or plain numbers
function formatChartValue(value: number) {
  if (Math.abs(value) >= 100) return `$${value.toLocaleString()}`;
  return value.toLocaleString();
}

// Render a chart from parsed JSON data
function ChartRenderer({ chartData }: { chartData: { type: string; title?: string; data: any[]; keys?: string[] } }) {
  const { type, title, data, keys } = chartData;

  if (!data || data.length === 0) return null;

  // Normalize type name to handle variations
  const normalizedType = type.toLowerCase().replace(/[_\s-]/g, '');

  const chartHeight = normalizedType === 'horizontalbar' || normalizedType === 'horizontal' || normalizedType === 'hbar' ? Math.max(250, data.length * 30) : 280;

  // Auto-detect the label key - `name` is canonical but LLMs often emit
  // `year`, `month`, `period`, etc.
  const nameKey = detectNameKey(data);

  const renderChart = () => {
    switch (normalizedType) {
      case 'pie':
      case 'piechart':
      case 'donut':
      case 'donutchart':
        return (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey={nameKey} cx="35%" cy="50%"
              outerRadius={90} innerRadius={normalizedType === 'donut' || normalizedType === 'donutchart' ? 50 : 0}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        );

      case 'bar':
      case 'barchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>}
          </BarChart>
        );

      case 'stackedbar':
      case 'stacked':
      case 'stackedbarchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} stackId="stack" fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false}/>) : <Bar dataKey="value" stackId="stack" fill={CHART_COLORS[0]} activeBar={false}/>}
          </BarChart>
        );

      case 'waterfall':
      case 'waterfallchart': {
        // Transform waterfall data: each bar starts where the previous one ended
        const waterfallData: any[] = [];
        data.forEach((item: any, i: number) => {
          const isTotal = item.isTotal || item.total || (item.name || '').toLowerCase().includes('total') || (item.name || '').toLowerCase().includes('net');
          if (i === 0 || isTotal) {
            waterfallData.push({ ...item, start: 0, end: item.value, fill: isTotal ? '#6366f1' : (item.value >= 0 ? '#22c55e' : '#ef4444') });
          } else {
            const prevEnd = waterfallData[i - 1]?.end || 0;
            waterfallData.push({ ...item, start: prevEnd, end: prevEnd + item.value, fill: item.value >= 0 ? '#22c55e' : '#ef4444' });
          }
        });
        return (
          <BarChart data={waterfallData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 5 ? -30 : 0} textAnchor={data.length > 5 ? 'end' : 'middle'} height={data.length > 5 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number, name: string) => [formatChartValue(Math.abs(v)), name === 'start' ? 'Base' : 'Value']} />
            <Bar dataKey="start" stackId="waterfall" fill="transparent" activeBar={false}/>
            <Bar dataKey="value" stackId="waterfall" radius={[4, 4, 0, 0]} activeBar={false}>
              {waterfallData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
            </Bar>
          </BarChart>
        );
      }

      case 'horizontalbar':
      case 'horizontal':
      case 'hbar':
        return (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 10 }} width={120} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false}/>}
          </BarChart>
        );

      case 'line':
      case 'linechart':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />) : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />}
          </LineChart>
        );

      case 'area':
      case 'areachart':
        // Use LineChart as area fallback
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />) : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />}
          </LineChart>
        );

      case 'scatter':
      case 'scatterchart':
      case 'dot':
      case 'dotchart':
      case 'dots':
      case 'bubble':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={0} dot={{ r: 6, fill: CHART_COLORS[i % CHART_COLORS.length] }} />) : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={0} dot={{ r: 6, fill: CHART_COLORS[0] }} />}
          </LineChart>
        );

      case 'radar':
      case 'radarchart':
      case 'spider':
      case 'spiderchart':
      case 'web':
        // Render as horizontal bar chart (radar/spider not supported in this recharts version)
        return (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 10 }} width={120} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false}/>}
          </BarChart>
        );

      default:
        // Default to bar chart for unknown types
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}/>
          </BarChart>
        );
    }
  };

  return (
    <div className="my-3 p-3 bg-muted/30 rounded-lg border">
      {title && <h4 className="text-sm font-semibold mb-2 text-center">{title}</h4>}
      <ResponsiveContainer width="100%" height={chartHeight}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}

// Delegated to the shared parser in ChatRenderer so the missing-closing-tag
// fallback + balanced-JSON scan stays in one place.
import { parseContentWithCharts as parseContentWithChartsShared, detectNameKey } from './ChatRenderer';
const parseContentWithCharts = parseContentWithChartsShared;

// Convert raw URLs in text to short markdown links
function shortenRawUrls(text: string): string {
  // Don't touch URLs already in markdown link format [text](url)
  // Only convert standalone raw URLs
  return text.replace(
    /(?<!\]\()(?<!\()(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*))/g,
    (url) => {
      // Skip if it's inside a markdown link already
      const before = text.substring(Math.max(0, text.indexOf(url) - 2), text.indexOf(url));
      if (before.endsWith('](') || before.endsWith('](')) return url;

      // Generate short label
      try {
        const u = new URL(url);
        if (u.hostname.includes('ato.gov.au')) return `[ATO Source](${url})`;
        if (u.hostname.includes('xero.com')) return `[Xero](${url})`;
        const host = u.hostname.replace('www.', '');
        return `[${host}](${url})`;
      } catch {
        return `[Link](${url})`;
      }
    }
  );
}

// Format message content with markdown rendering and inline citations
function FormattedMessage({
  content,
  citations,
  onCitationClick,
}: {
  content: string;
  citations?: DocumentCitation[];
  onCitationClick: (index: number) => void;
}) {
  const components: Components = useMemo(() => ({
    table: ({ children, ...props }) => (
      <div className="my-2 overflow-x-auto w-full not-prose">
        <table {...props} className="text-xs w-full border-collapse" style={{ border: '1px solid #d1d5db' }}>
          {children}
        </table>
      </div>
    ),
    th: ({ children, ...props }) => (
      <th {...props} className="text-left p-2 font-semibold bg-zinc-100 dark:bg-zinc-800 text-xs" style={{ border: '1px solid #d4d4d8' }}>{children}</th>
    ),
    td: ({ children, ...props }) => (
      <td {...props} className="p-2 text-xs align-top" style={{ border: '1px solid #d4d4d8' }}>{children}</td>
    ),
    pre: ({ children, ...props }) => (
      <pre {...props} className="my-2 p-3 bg-muted text-foreground rounded-md overflow-x-auto text-xs">
        {children}
      </pre>
    ),
    code: ({ children, className, ...props }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) return <code className={className} {...props}>{children}</code>;
      return <code className="text-primary bg-primary/10 px-1 py-0.5 rounded text-xs" {...props}>{children}</code>;
    },
    h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-1">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-bold mt-2 mb-1">{children}</h3>,
    h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-0.5">{children}</h4>,
    p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
    ul: ({ children }) => <ul className="my-1 ml-4 list-disc space-y-0.5">{children}</ul>,
    ol: ({ children }) => <ol className="my-1 ml-4 list-decimal space-y-0.5">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/30 pl-3 my-2 italic text-muted-foreground">{children}</blockquote>,
    hr: () => <hr className="my-3 border-border" />,
  }), []);

  const parts = useMemo(() => parseContentWithCharts(content), [content]);

  return (
    <div>
      {parts.map((part, i) =>
        part.type === 'chart' && part.chartData ? (
          <ChartRenderer key={i} chartData={part.chartData} />
        ) : (
          <div key={i} className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1 prose-ol:my-1 prose-headings:my-1 overflow-x-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {shortenRawUrls(part.content)}
            </ReactMarkdown>
          </div>
        )
      )}
      {citations && citations.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {citations.map((citation, index) => (
            <InlineCitation
              key={index}
              index={index + 1}
              citation={citation}
              onClick={() => onCitationClick(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export type DataSource = 'both' | 'xero' | 'documents';

/** A labelled on/off row used to pick which data sources the chat draws from. */
function SourceToggle({
  icon, label, hint, on, disabled, onToggle, activeColor,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  activeColor?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-2.5 py-1.5',
        disabled ? 'opacity-60' : '',
      )}
    >
      <span className={cn('flex-shrink-0', on ? 'text-foreground' : 'text-muted-foreground')}>{icon}</span>
      <div className="flex-1 min-w-0 leading-tight">
        <p className="text-xs font-medium truncate">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground truncate">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-pressed={on}
        title={disabled ? 'Not connected' : on ? 'Turn off' : 'Turn on'}
        className={cn(
          'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed',
          on ? '' : 'bg-muted-foreground/30',
        )}
        style={on ? { backgroundColor: activeColor || 'hsl(var(--primary))' } : undefined}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
}

export function ClientFileChatSidebar({ clientId, clientName, accountingProvider }: ClientFileChatSidebarProps) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const hasAccounting = !!accountingProvider;
  const accountingLabel = accountingProvider === 'qbo' ? 'QuickBooks' : 'Xero';
  // Two independent on/off toggles drive a single dataSource value:
  //   docs + accounting -> 'both', docs only -> 'documents', accounting only -> 'xero'.
  // 'xero' is the backend's generic "accounting" key (auto-resolves Xero or QBO).
  const [dataSource, setDataSource] = useState<DataSource>(hasAccounting ? 'both' : 'documents');
  const docsOn = dataSource === 'both' || dataSource === 'documents';
  const acctOn = hasAccounting && (dataSource === 'both' || dataSource === 'xero');
  const sourceFrom = (docs: boolean, acct: boolean): DataSource =>
    docs && acct ? 'both' : acct ? 'xero' : 'documents';
  const toggleDocs = () => {
    // Keep at least one source on.
    if (docsOn && !acctOn) return;
    setDataSource(sourceFrom(!docsOn, acctOn));
  };
  const toggleAcct = () => {
    if (!hasAccounting) return;
    if (acctOn && !docsOn) return;
    setDataSource(sourceFrom(docsOn, !acctOn));
  };
  const [selectedCitation, setSelectedCitation] = useState<{
    citation: DocumentCitation;
    index: number;
  } | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Conversation history state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Load conversation list
  useEffect(() => {
    clientsService.listConversations(clientId).then(setConversations).catch(() => {});
  }, [clientId]);

  // Auto-save conversation after AI response
  const saveConversation = async (messages: ChatMessage[]) => {
    if (messages.length < 2) return;
    try {
      const firstUserMsg = messages.find(m => m.role === 'user');
      const words = (firstUserMsg?.content || '').split(/\s+/).slice(0, 4).join(' ');
      const title = words ? (words + (words.length < (firstUserMsg?.content?.length || 0) ? '...' : '')) : 'Chat';
      const result = await clientsService.saveConversation(clientId, {
        id: conversationId || undefined,
        title,
        messages,
        dataSource,
      });
      if (!conversationId && result?.id) {
        setConversationId(result.id);
      }
      // Refresh list
      clientsService.listConversations(clientId).then(setConversations).catch(() => {});
    } catch {
      // Silent fail - don't interrupt chat
    }
  };

  // Load a past conversation
  const loadConversation = async (convId: string) => {
    try {
      const conv = await clientsService.getConversation(clientId, convId);
      setConversationHistory(conv.messages || []);
      setConversationId(conv.id);
      setDataSource(conv.data_source || 'both');
      setShowHistory(false);
    } catch {
      toast.error('Failed to load conversation');
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setConversationHistory([]);
    setConversationId(null);
    setShowHistory(false);
  };

  // Auto-scroll chat area to bottom when new messages arrive
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [conversationHistory]);

  const chatMutation = useMutation({
    mutationFn: (msg: string) =>
      clientsService.chatWithClientFiles({
        clientId,
        message: msg,
        conversationHistory,
        dataSource,
      }),
    onSuccess: (data) => {
      const updated = [...conversationHistory, data.message];
      setConversationHistory((prev) => [...prev, data.message]);
      setMessage('');
      // Auto-save after AI response
      saveConversation(updated);
      // Update usage counter in header
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
    },
    onError: () => {
      toast.error('Failed to send message');
    },
  });

  const handleSendMessage = () => {
    if (!message.trim() || chatMutation.isPending) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    setConversationHistory((prev) => [...prev, userMessage]);
    chatMutation.mutate(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCitationClick = (citations: DocumentCitation[], index: number) => {
    setSelectedCitation({ citation: citations[index], index: index + 1 });
  };

  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      {/* Header */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base">Ask about files</CardTitle>
              <CardDescription className="text-xs">AI-powered search</CardDescription>
            </div>
          </div>
          {/* Data sources - two independent on/off toggles */}
          <div className="mt-2 space-y-1.5">
            <SourceToggle
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Documents"
              on={docsOn}
              onToggle={toggleDocs}
            />
            <SourceToggle
              icon={<Link2 className="h-3.5 w-3.5" />}
              label={hasAccounting ? accountingLabel : 'Accounting software'}
              hint={hasAccounting ? undefined : 'Not connected'}
              on={acctOn}
              disabled={!hasAccounting}
              onToggle={toggleAcct}
              activeColor={accountingProvider === 'qbo' ? '#2CA01C' : '#13B5EA'}
            />
          </div>
        </CardHeader>
      </Card>

      {/* Messages - Scrollable Area */}
      <Card className="flex-1 mb-4 overflow-hidden">
        <CardContent className="h-full p-0">
          <ScrollArea className="h-full px-4" ref={scrollAreaRef}>
          {conversationHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <h4 className="font-semibold mb-1 text-sm">Ask me anything</h4>
              <p className="text-xs text-muted-foreground mb-4 px-2">
                I can search through all files to find answers
              </p>
              <div className="space-y-2 w-full">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs justify-start h-8"
                  onClick={() => setMessage("What's the ABN?")}
                >
                  What's the ABN?
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs justify-start h-8"
                  onClick={() => setMessage('Show me the latest BAS filing')}
                >
                  Latest BAS filing
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs justify-start h-8"
                  onClick={() => setMessage('What was their total income last year?')}
                >
                  Total income last year
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-4">
              {conversationHistory.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    'flex gap-2',
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {msg.role === 'assistant' && (
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                    </div>
                  )}
                  <div
                    data-msg-id={msg.id}
                    className={cn(
                      'max-w-[85%] rounded-lg p-2.5 text-xs relative group/msg',
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    )}
                    data-msg-id={msg.id}
                  >
                    {msg.role === 'assistant' && (
                      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={async () => {
                            await navigator.clipboard.writeText(msg.content);
                            setCopiedMessageId(msg.id);
                            setTimeout(() => setCopiedMessageId(null), 2000);
                          }}
                          title="Copy"
                        >
                          {copiedMessageId === msg.id ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Export">
                              <Download className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[140px]">
                            <DropdownMenuItem onClick={() => {
                              const msgEl = document.querySelector(`[data-msg-id="${msg.id}"]`) as HTMLElement;
                              toast.promise(
                                exportToPdf({ content: msg.content, clientName, messageElement: msgEl }),
                                { loading: 'Generating PDF...', success: 'PDF exported', error: 'PDF export failed' }
                              );
                            }}>
                              <FileDown className="h-3.5 w-3.5 mr-2 text-red-500" />
                              Export as PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              const msgEl = document.querySelector(`[data-msg-id="${msg.id}"]`) as HTMLElement;
                              toast.promise(
                                exportToWord({ content: msg.content, clientName, messageElement: msgEl }),
                                { loading: 'Generating Word...', success: 'Word exported', error: 'Word export failed' }
                              );
                            }}>
                              <FileType className="h-3.5 w-3.5 mr-2 text-blue-500" />
                              Export as Word
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              exportToCsv({ content: msg.content, clientName });
                              toast.success('CSV exported');
                            }}>
                              <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-green-500" />
                              Export as CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              toast.promise(
                                exportToExcel({ content: msg.content, clientName }),
                                { loading: 'Generating Excel...', success: 'Excel exported', error: 'Excel export failed' }
                              );
                            }}>
                              <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                              Export as Excel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    {msg.role === 'assistant' ? (
                      <FormattedMessage
                        content={msg.content}
                        citations={msg.citations}
                        onCitationClick={(index) =>
                          msg.citations && handleCitationClick(msg.citations, index)
                        }
                      />
                    ) : (
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    )}

                    {/* Citation summary footer */}
                    {msg.citations && msg.citations.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-[10px] text-muted-foreground">Sources:</span>
                          {msg.citations.map((citation, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleCitationClick(msg.citations!, idx)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-background/50 hover:bg-background rounded border border-border/50 transition-colors"
                            >
                              {citation.source === 'benchmark' ? (
                                <BarChart3 className="h-2.5 w-2.5 text-violet-500" />
                              ) : (
                                <FileText className="h-2.5 w-2.5" />
                              )}
                              <span className="max-w-[100px] truncate">{citation.documentName.split('.')[0]}</span>
                              <ChevronRight className="h-2.5 w-2.5 opacity-50" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-[10px] opacity-60 mt-1">{formatTime(msg.timestamp)}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-semibold text-primary-foreground">
                        {clientName.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {chatMutation.isPending && (
                <div className="flex gap-2">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="bg-muted rounded-lg p-2.5">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
        </CardContent>
      </Card>

      {/* Input - Fixed at Bottom */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2 items-end">
            <Textarea
              ref={inputRef}
              placeholder="Ask about client files... (Shift+Enter for new line)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={chatMutation.isPending}
              className="flex-1 min-h-[60px] max-h-[150px] resize-none text-xs"
              rows={2}
            />
            <Button
              size="icon"
              onClick={handleSendMessage}
              disabled={!message.trim() || chatMutation.isPending}
              className="h-9 w-9"
            >
              {chatMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            AI searches indexed documents • Click <span className="inline-flex items-center justify-center h-3 w-3 text-[8px] font-bold bg-primary/20 text-primary rounded-sm">1</span> for sources
          </p>
        </CardContent>
      </Card>

      {/* Citation Detail Dialog */}
      {selectedCitation && (
        <CitationDialog
          citation={selectedCitation.citation}
          index={selectedCitation.index}
          open={!!selectedCitation}
          onOpenChange={(open) => !open && setSelectedCitation(null)}
        />
      )}

      {/* History icon - inside chat messages card, bottom right */}
      {!showHistory && (
        <button
          onClick={() => setShowHistory(true)}
          className="absolute bottom-[150px] right-4 z-30 text-muted-foreground/50 hover:text-primary transition-colors"
          title="Chat History"
        >
          <History className="h-4 w-4" />
          {conversations.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-destructive text-[7px] text-white flex items-center justify-center font-bold">
              {conversations.length > 9 ? '9+' : conversations.length}
            </span>
          )}
        </button>
      )}

      {/* Sliding History Panel - slides from right */}
      <div
        className={cn(
          'absolute top-0 right-0 h-full w-72 bg-background border-l shadow-xl z-40 flex flex-col transition-transform duration-300 ease-in-out',
          showHistory ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Chat History</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { startNewConversation(); setShowHistory(false); }}>
              <Plus className="h-3 w-3 mr-1" />
              New
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowHistory(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Conversation List */}
        <ScrollArea className="flex-1 p-2">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageCircle className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">No past conversations</p>
              <p className="text-[10px] text-muted-foreground mt-1">Start chatting to save history</p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/80 text-xs group transition-colors',
                    conversationId === conv.id && 'bg-primary/10 border border-primary/20'
                  )}
                  onClick={() => loadConversation(conv.id)}
                >
                  <MessageCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium max-w-[160px]">{conv.title}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(conv.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {' · '}{conv.message_count || 0} msgs
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 opacity-30 hover:opacity-100 flex-shrink-0"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await clientsService.deleteConversation(clientId, conv.id);
                      setConversations(prev => prev.filter(c => c.id !== conv.id));
                      if (conversationId === conv.id) startNewConversation();
                    }}
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
